import { Controller } from 'stimulus';
import { useResizeObserver } from '@/js/util';
import { Drag, TrackElement, Wheel } from './carousel';

const DRAGGING_CLASS = 'cursor-grabbing';

/**
 * A light implementation of carousel. For inspiration thanks to
 * {@link https://github.com/pawelgrzybek/siema siema} and
 * {@link https://swiperjs.com/ swiper}.
 */
export default class extends Controller {
  static targets = ['window', 'slide', 'counter', 'initial'];

  initialize() {
    this.activeSlide = 0;
    this.windowWidth = null;
    this.started = false;
  }

  windowTargetConnected(element) {
    this.windowWidth = element.offsetWidth;
    this.track = new TrackElement(element.firstElementChild);
  }

  initialTargetConnected() {
    this.#start();
  }

  connect() {
    useResizeObserver(this);

    this.drag = new Drag(10);
    this.wheel = new Wheel(10);

    const resizeObserver = this.createResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target !== this.element) {
          continue;
        }

        const windowWidthBefore = this.windowWidth;
        this.windowWidth = entry.borderBoxSize[0].inlineSize;

        if (this.windowWidth !== windowWidthBefore) {
          this.#slideToCurrent();
        }
      }
    });
    resizeObserver.observe(this.element);

    if (!this.track.isTurboFrame) {
      this.#start();
    }
  }

  disconnect() {
    this.wheel.reset();
  }

  get slidesCount() {
    return this.slideTargets.length;
  }

  /**
   * Show previous slide.
   */
  prev() {
    if (!this.started) {
      return;
    }

    const prevSlide = Math.max(0, this.activeSlide - 1);
    this.#maybeSlideTo(prevSlide, true);
  }

  /**
   * Show next slide.
   */
  next() {
    if (!this.started) {
      return;
    }

    const nextSlide = Math.min(this.slidesCount - 1, this.activeSlide + 1);
    this.#maybeSlideTo(nextSlide, true);
  }

  /**
   * Show initial slide.
   */
  reset() {
    if (!this.started || !this.hasInitialTarget) {
      return;
    }

    const initialSlideIndex = this.slideTargets.indexOf(this.initialTarget);
    this.#maybeSlideTo(initialSlideIndex);
  }

  startDrag(e) {
    if (!this.started) {
      return;
    }

    this.drag.started(e);
    this.windowTarget.classList.add(DRAGGING_CLASS);
  }

  trackDrag(e) {
    if (!this.drag.active) {
      return;
    }

    if (this.drag.moved(e)) {
      this.#slideByDrag();
    } else {
      this.#slideToCurrent();
    }
  }

  stopDrag(e) {
    if (this.drag.active) {
      this.#slideAfterDrag();
    }

    this.windowTarget.classList.remove(DRAGGING_CLASS);
    this.drag.reset();
  }

  handleWheel(e) {
    if (!this.started || this.drag.active) {
      return;
    }

    const side = this.wheel.moved(e, this.windowWidth);

    if (side === 'top' || side === 'bottom') {
      return;
    }

    e.preventDefault();

    if (this.wheel.underThreshold(this.windowWidth)) {
      return;
    }

    if (side === 'right') {
      this.next();
    } else {
      this.prev();
    }

    this.wheel.lock();
  }

  #start() {
    if (this.hasInitialTarget) {
      this.#slideTo(this.track.indexOf(this.initialTarget));
    }

    this.#updateCounter();
    this.started = true;
  }

  #maybeSlideTo(index, transition = false) {
    if (index === this.activeSlide) {
      return;
    }

    this.#slideTo(index, transition);
  }

  #slideTo(index, transition = false) {
    this.activeSlide = index;
    this.#slideToCurrent(transition);
    this.#updateCounter();
    this.dispatch('slide-shown', { detail: { slide: this.slideTargets[index] } });
  }

  #slideAfterDrag() {
    if (this.drag.underThreshold(this.windowWidth)) {
      this.#slideToCurrent(true);
      return;
    }

    const target =
      this.drag.distance > 0
        ? Math.max(0, this.activeSlide - 1)
        : Math.min(this.slidesCount - 1, this.activeSlide + 1);

    // Dragging past the first or last slide clamps to the current one: snap the track back
    // without announcing a slide, which listeners read as "a new slide is up".
    if (target === this.activeSlide) {
      this.#slideToCurrent(true);
      return;
    }

    this.#slideTo(target, true);
  }

  #slideByDrag() {
    const translateValue = this.windowWidth * this.activeSlide - this.drag.distance;
    const gapAdjustment = this.track.gap * this.activeSlide;
    this.track.move(translateValue + gapAdjustment);
  }

  #slideToCurrent(transition = false) {
    const translateValue = this.windowWidth * this.activeSlide;
    const gapAdjustment = this.track.gap * this.activeSlide;
    this.track.move(translateValue + gapAdjustment, transition);
  }

  #updateCounter() {
    if (this.hasCounterTarget) {
      this.counterTarget.innerText = `${this.activeSlide + 1} of ${this.slidesCount}`;
    }
  }
}
